import { z } from 'zod';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { basePriceSchema, taxesSchema } from '../../../forms/schemas/configureProductFormSchema';
import type { PricingProductFormValues, EditPricingProps } from '../types';
import { RepricingFieldName } from '../repricingOptions';
import { EditPricingFormContent } from './FormContent';
import { mapTaxRates } from './utils';

export const pricingConfigurationSchema = basePriceSchema.merge(taxesSchema).extend({
    [RepricingFieldName.RepriceExistingReservations]: z.boolean(),
});

export const EditProductPricingForm = ({
    propertyId,
    enterpriseProductId,
    value,
    taxRateCodes,
    centralProductId,
    ...formProps
}: EditPricingProps) => {
    const { taxEnvironmentCode: enterpriseTaxEnvironmentCode } = useEnterprise(propertyId);

    const { mutateAsync } = useRepositoryMutation('portfolioEnterpriseProducts', 'updateBasePrice', {
        onSuccess: () => showSuccessAlert({ title: lt('ChangesSaved') }),
        invalidateQueries: {
            portfolioEnterpriseProducts: ['getDetail'],
        },
    });

    const taxRate = mapTaxRates(value.taxValues, taxRateCodes);

    const { renderFormContent } = useHandledDialogFormControls<PricingProductFormValues>({
        ...formProps,
        values: value && {
            price: value?.netValue,
            currencyCode: value?.currency,
            repriceExistingReservations: false,
            taxEnvironmentCode: {
                [enterpriseTaxEnvironmentCode]: {
                    taxRate,
                },
            },
        },
        onSubmit: async ({ price, repriceExistingReservations, taxEnvironmentCode }) => {
            const [envCode] = Object.keys(taxEnvironmentCode || {});
            const taxRates = envCode ? Object.values(taxEnvironmentCode[envCode]?.taxRate ?? {}) : [];

            await mutateAsync({
                centralProductId,
                currencyCode: value?.currency || '',
                enterpriseProductId,
                price,
                propertyId,
                taxEnvironmentCode: envCode,
                taxRates,
                repriceExistingReservations: Boolean(repriceExistingReservations),
            });
        },

        resolver: createZodFormResolver(pricingConfigurationSchema),
    });

    return renderFormContent(
        <EditPricingFormContent value={value} taxEnvironmentCode={enterpriseTaxEnvironmentCode} />
    );
};
